import { useLocation, useRouter } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { PageLoader } from '@customerio/pluma-components/react';
import { PlanGuard } from '@/components/guards';
import { BUILDER_PLAN_SLUG } from '@/services/auth/plan-slugs';
import { ProtectedRoute } from '@/services/router/components/protected-route';
import { SafeNavigate } from '@/services/router/safe-navigate';
import { generatePath } from '@/services/router/generate-path';
import { getWorkspaceIdFromUrl } from '@/utils/workspace-id';
import { useCurrentWorkspace } from '@/context/current-workspace';
import { RumPageRendered } from '@/services/rum/use-page-rendered';
import ReportingWebhooksPage from './index.page';
import NewReportingWebhookPage from './new.page';
import EditReportingWebhookPage from './edit.page';

const LOADING_LABEL = 'Loading reporting webhooks…';

/** Which of the three pages a legacy URL is forwarding to. */
export type ReportingWebhooksPageKind = 'index' | 'new' | 'edit';

type HandoffTarget = {
  kind: ReportingWebhooksPageKind;
  /** Only meaningful for `edit`. */
  webhookId?: string;
};

/** The migrated URL for a page — the same one minus the `journeys` segment. */
function useMigratedHref({ kind, webhookId }: HandoffTarget): string | null {
  const router = useRouter();
  const { pathname, searchStr, hash } = useLocation();
  const workspaceId = getWorkspaceIdFromUrl(pathname);

  if (workspaceId == null) {
    return null;
  }

  const params = { workspaceId, webhookId: webhookId ?? '' };
  const to = {
    index: '/workspaces/$workspaceId/integrations/reporting-webhooks',
    new: '/workspaces/$workspaceId/integrations/reporting-webhooks/new',
    edit: '/workspaces/$workspaceId/integrations/reporting-webhooks/$webhookId',
  }[kind];

  const href = router.buildLocation({ to, params }).href;
  // Ember transitions preserve query params, so the forward carries the query string and hash.
  return `${href}${searchStr}${hash === '' ? '' : `#${hash}`}`;
}

type JourneysReportingWebhooksHandoffProps = HandoffTarget;

/**
 * The old `/journeys/integrations/reporting-webhooks…` URLs. Hydra claims them purely to forward to
 * the migrated route, so Ember-side links and existing bookmarks keep working without every caller
 * being re-pointed. Ember no longer serves these paths at all (ui#16224).
 */
export function JourneysReportingWebhooksHandoff(target: JourneysReportingWebhooksHandoffProps) {
  const to = useMigratedHref(target);

  // `/workspaces/last` is rewritten by a bridge-synced navigation that leaves route params stale,
  // so the id is read off the reactive location and held until it lands.
  if (to == null) {
    return <PageLoader label={LOADING_LABEL} />;
  }

  return <SafeNavigate to={to} options={{ shouldReplaceHistoryEntry: true }} />;
}

type ReportingWebhooksRouteViewProps = {
  children: ReactNode;
};

/**
 * The migrated `/integrations/reporting-webhooks…` URLs. Hydra owns them unconditionally, applying
 * the same route-access gates Ember's `integrations` root route did (reporting-webhooks nested
 * under it, so it inherited both): Builder-plan accounts go to the available-plans page, and
 * accounts missing `journeys/imports/view` are redirected away.
 */
function ReportingWebhooksRouteView({ children }: ReportingWebhooksRouteViewProps) {
  const workspace = useCurrentWorkspace();

  // Gate on the resolved workspace, not the URL segment: the pages' reads go through
  // `requireWorkspaceId`, which throws while the workspaces list is still loading and while the
  // `/workspaces/last` sentinel leaves route params stale.
  if (workspace == null) {
    return <PageLoader label={LOADING_LABEL} />;
  }

  return (
    <PlanGuard excludedPlans={[BUILDER_PLAN_SLUG]}>
      <ProtectedRoute
        permission="journeys/imports/view"
        redirectTo={generatePath('/workspaces/:workspaceId', { workspaceId: String(workspace.id) })}
      >
        {children}
      </ProtectedRoute>
    </PlanGuard>
  );
}

export function ReportingWebhooksIndexRouteView() {
  return (
    <ReportingWebhooksRouteView>
      <RumPageRendered>
        <ReportingWebhooksPage />
      </RumPageRendered>
    </ReportingWebhooksRouteView>
  );
}

export function NewReportingWebhookRouteView() {
  return (
    <ReportingWebhooksRouteView>
      <RumPageRendered>
        <NewReportingWebhookPage />
      </RumPageRendered>
    </ReportingWebhooksRouteView>
  );
}

type EditReportingWebhookRouteViewProps = {
  webhookId: string;
};

export function EditReportingWebhookRouteView({ webhookId }: EditReportingWebhookRouteViewProps) {
  return (
    <ReportingWebhooksRouteView>
      <RumPageRendered>
        <EditReportingWebhookPage webhookId={webhookId} />
      </RumPageRendered>
    </ReportingWebhooksRouteView>
  );
}
